import { alpha, Box, Button, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow } from '@mui/material';
import Grid from '@mui/material/Grid2';
import 'jspdf-autotable';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MainBorderRadius, TableHeader } from '../../../app/AppStyles';
import { DisplayName, GetDate, GetTime } from '../../../app/AppUtils';
import AutocompleteSelector from '../../subcomponents/AutocompleteSelector';
import ContentLoader from '../../subcomponents/ContentLoader';
import TablePaginationActions from '../../subcomponents/TablePaginationActions';
import { fetchReportPaidActivities, fetchReportPerformances, resetReportFilterValues } from '../reportFiltersSlice';
import ExportExcelIdealTransactions from './ExportExcelIdealTransactions';
import { fetchIdealTransasctionsReport, resetIdealTransactions } from './reportIdealTransactionsSlice';

export default function ReportIdealTransactionsTab() {
    const dispatch = useDispatch();

    const [searching, setSearching] = useState(false);
    const [selectedActivity, setSelectedActivity] = useState();
    const [selectedPerformances, setSelectedPerformances] = useState();

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { idealTransactionsReport, idealTransactionsReportStatus } = useSelector((state) => state.reports.transactions);
    const { reportPaidActivities, reportPaidActivitiesStatus, reportPerformances, reportPerformancesStatus } = useSelector((state) => state.reports.reportFilters);

    useEffect(() => {
        dispatch(resetReportFilterValues());
        dispatch(fetchReportPaidActivities());
    }, [dispatch]);

    useEffect(() => {
        if (selectedActivity)
            dispatch(fetchReportPerformances({ activityId: selectedActivity.activityId, key: "transactions" }));
    }, [dispatch, selectedActivity]);

    useEffect(() => {
        dispatch(resetIdealTransactions())
    }, []);

    useEffect(() => {
        if (idealTransactionsReportStatus === "success" || idealTransactionsReportStatus === "failed")
            setSearching(false);
    }, [idealTransactionsReportStatus]);

    const handleSearchReportClick = () => {
        setSearching(true);
        dispatch(fetchIdealTransasctionsReport(selectedPerformances.map(x => x.performanceId)));
    };

    const TableFragment = () => {
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(idealTransactionsReport?.length < 100 ? -1 : 100);

        const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - idealTransactionsReport?.length) : 0;

        const handleChangePage = (event, newPage) => {
            setPage(newPage);
        };

        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        };

        return (
            <>
                <TableBody>
                    {(rowsPerPage > 0 ? idealTransactionsReport?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : idealTransactionsReport)?.map((item, index) =>
                        <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'white.main' }, height: 63 }}>
                            <TableCell align="center">{item.transactionId}</TableCell>
                            <TableCell align="center">{item.transactionStatus}</TableCell>
                            <TableCell align="center">{item.transactionAmount}</TableCell>
                            <TableCell align="center">{item.paidAmount ?? "-"}</TableCell>
                            <TableCell align="center">{DisplayName(item.studentFirstName, item.studentInsertion, item.studentLastName)}</TableCell>
                            <TableCell align="center">{item.activityName}</TableCell>
                            <TableCell align="center">{item.performanceName}</TableCell>
                            <TableCell align="center">{item.performanceNumber}</TableCell>
                            <TableCell align="center">{item.dateUnsubscribed ? `${GetDate(item.dateUnsubscribed)} (${GetTime(item.dateUnsubscribed)})` : "-"}</TableCell>
                            <TableCell align="center">{item.unsubscribedBy ?? "-"}</TableCell>
                            <TableCell align="center">{item.dateCreated ? `${GetDate(item.dateCreated)} (${GetTime(item.dateCreated)})` : "-"}</TableCell>
                            <TableCell align="center">{item.createdBy ?? "-"}</TableCell>
                            <TableCell align="center">{item.dateModified ? `${GetDate(item.dateModified)} (${GetTime(item.dateModified)})` : "-"}</TableCell>
                            <TableCell align="center">{item.modifiedBy ?? "-"}</TableCell>
                        </TableRow>
                    )}
                    {(rowsPerPage > 0 ? idealTransactionsReport?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : idealTransactionsReport)?.length === 0 && <TableRow><TableCell colSpan={13}>{resourceFinal[appLanguage].messages.FILTER_RESULT_EMPTY}</TableCell></TableRow>}
                    {emptyRows > 0 && <TableRow style={{ height: 63 * emptyRows }}><TableCell colSpan={10} /></TableRow>}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            sx={{ border: 0 }}
                            rowsPerPageOptions={[10, 25, 50,
                                {
                                    label: idealTransactionsReport?.length < 100 ? resourceFinal[appLanguage].words.ALL : '100',
                                    value: idealTransactionsReport?.length < 100 ? -1 : 100
                                }
                            ]}
                            count={idealTransactionsReport ? idealTransactionsReport?.length : 0}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelDisplayedRows={({ from, to, count }) => { return `${from}-${to} ${resourceFinal[appLanguage].messages.OF_THE} ${count}`; }}
                            labelRowsPerPage={resourceFinal[appLanguage].messages.TABLE_ROWS_PER_PAGE}
                        />
                    </TableRow>
                </TableFooter>
            </>
        );
    };

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
                <Grid container direction="row" spacing={1} sx={{ bgcolor: theme => alpha(theme.palette.white.main, 0.3), borderRadius: MainBorderRadius, p: 1 }}>
                    <Grid size={3}>
                        <AutocompleteSelector
                            label={resourceFinal[appLanguage].words.ACTIVITY}
                            options={reportPaidActivities ?? []}
                            value={selectedActivity ?? null}
                            onChange={(val) => {
                                setSelectedPerformances([]);
                                setSelectedActivity(val)
                            }}
                            getOptionLabel={(option) => option.activityName}
                            getOptionKey={(option) => option.activityId}
                            isOptionEqualToValue={(option, value) => option.activityId === value?.activityId}
                            disabled={reportPaidActivitiesStatus !== "success"}
                        />
                    </Grid>
                    <Grid size={3}>
                        <AutocompleteSelector
                            label={resourceFinal[appLanguage].words.PERFORMANCE}
                            options={reportPerformances ?? []}
                            value={selectedPerformances ?? []}
                            onChange={(val) => {
                                setSelectedPerformances(val)
                            }}
                            getOptionLabel={(option) => option.performanceName}
                            getOptionKey={(option) => option.performanceId}
                            isOptionEqualToValue={(option, value) => option.performanceId === value?.performanceId}
                            disabled={reportPerformancesStatus !== "success"}
                            multiple
                        />
                    </Grid>
                    <Grid size={6}>
                        <Button
                            variant="outlined"
                            color="secondary"
                            disabled={!selectedPerformances?.length > 0}
                            sx={{ mr: 1, height: 1 }}
                            onClick={handleSearchReportClick}
                        >
                            {resourceFinal[appLanguage].words.REPORT_GENERATE}
                        </Button>
                        <ExportExcelIdealTransactions disabled={searching || !idealTransactionsReport} jsonData={idealTransactionsReport} />
                    </Grid>
                </Grid>
                <ContentLoader
                    successCondition={searching === false}
                    errorCondition={idealTransactionsReport === "failed"}
                    content={
                        <TableContainer>
                            <Table size="small" id="#report">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.TRANSACTION}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.STATUS}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.TRANSACTION}{resourceFinal[appLanguage].words.AMOUNT.toLowerCase()}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.PAID}{resourceFinal[appLanguage].words.AMOUNT.toLowerCase()}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.STUDENT}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.ACTIVITY}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.PERFORMANCE}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.PERFORMANCE}{resourceFinal[appLanguage].words.NUMBER.toLowerCase()}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.UNSUBSCRIBED_DATE}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.UNSUBSCRIBED_BY}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.DATE_CREATED}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.CREATED_BY}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.DATE_MODIFIED}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.MODIFIED_BY}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableFragment />
                            </Table>
                        </TableContainer>
                    }
                />
            </Box>
        </>
    );
}
